/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { IconQuestionMark, Text, Tooltip, TooltipSide } from '@sonarsource/echoes-react';
import classNames from 'classnames';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { Card, SnoozeCircleIcon, TrendDownCircleIcon, TrendUpCircleIcon } from '~design-system';
import {
  GridContainer,
  StyleMeasuresCard,
  StyleMeasuresCardRightBorder,
  StyledConditionsCard,
} from '~shared/components/overview/BranchSummaryStyles';
import { getBranchLikeQuery } from '~shared/helpers/branch-like';
import { PullRequest } from '~shared/types/branch-like';
import { MeasureEnhanced } from '~shared/types/measures';
import { MetricKey, MetricType } from '~shared/types/metrics';
import { getLeakValue } from '~sq-server-commons/components/measure/utils';
import FailedConditions from '~sq-server-commons/components/overview/FailedConditions';
import { IssueMeasuresCardInner } from '~sq-server-commons/components/overview/IssueMeasuresCardInner';
import { MeasuresCardDependencyRisk } from '~sq-server-commons/components/overview/MeasuresCardDependencyRisk';
import MeasuresCardNumber from '~sq-server-commons/components/overview/MeasuresCardNumber';
import MeasuresCardPercent from '~sq-server-commons/components/overview/MeasuresCardPercent';
import { DEFAULT_ISSUES_QUERY } from '~sq-server-commons/components/shared/utils';
import { findMeasure } from '~sq-server-commons/helpers/measures';
import { getComponentDrilldownUrl } from '~sq-server-commons/helpers/urls';
import { formatMeasure } from '~sq-server-commons/sonar-aligned/helpers/measures';
import {
  getComponentIssuesUrl,
  getComponentSecurityHotspotsUrl,
} from '~sq-server-commons/sonar-aligned/helpers/urls';
import { QualityGateStatusConditionEnhanced } from '~sq-server-commons/types/quality-gates';
import { Component, QualityGate } from '~sq-server-commons/types/types';
import {
  MeasurementType,
  QGStatusEnum,
  getConditionRequiredLabel,
  getMeasurementMetricKey,
} from '~sq-server-commons/utils/overview-utils';

interface Props {
  component: Component;
  conditions: QualityGateStatusConditionEnhanced[];
  measures: MeasureEnhanced[];
  pullRequest: PullRequest;
  qualityGate?: QualityGate;
}

export default function MeasuresCardPanel(props: React.PropsWithChildren<Props>) {
  const { pullRequest, component, measures, conditions, qualityGate } = props;

  const newSecurityHotspots = getLeakValue(
    findMeasure(measures, MetricKey.new_security_hotspots),
  ) as string;

  const intl = useIntl();

  const issuesCount = getLeakValue(findMeasure(measures, MetricKey.new_violations));
  const issuesCondition = conditions.find((c) => c.metric === MetricKey.new_violations);
  const isIssuesConditionFailed = issuesCondition?.level === QGStatusEnum.ERROR;
  const fixedCount = findMeasure(measures, MetricKey.pull_request_fixed_issues)?.value;
  const acceptedCount = getLeakValue(findMeasure(measures, MetricKey.new_accepted_issues));
  const dependencyRiskCount = getLeakValue(
    findMeasure(measures, MetricKey.new_sca_count_any_issue),
  );
  const dependencyRiskRating = getLeakValue(
    findMeasure(measures, MetricKey.new_sca_rating_any_issue),
  );

  const issuesUrl = getComponentIssuesUrl(component.key, {
    ...getBranchLikeQuery(pullRequest),
    ...DEFAULT_ISSUES_QUERY,
  });
  const fixedUrl = getComponentIssuesUrl(component.key, {
    fixedInPullRequest: pullRequest.key,
  });
  const acceptedUrl = getComponentIssuesUrl(component.key, {
    ...getBranchLikeQuery(pullRequest),
    ...DEFAULT_ISSUES_QUERY,
    issueStatuses: 'ACCEPTED',
  });

  const totalFailedConditions = conditions.filter(
    (condition) => condition.level === QGStatusEnum.ERROR,
  );

  return (
    <Card className="sw-py-8 sw-px-6">
      <GridContainer
        className={classNames('sw-relative sw-overflow-hidden js-summary', {
          'sw-grid-cols-3': totalFailedConditions.length === 0,
          'sw-grid-cols-4': totalFailedConditions.length > 0,
        })}
      >
        {totalFailedConditions.length > 0 && (
          <StyledConditionsCard className="sw-row-span-3">
            <FailedConditions
              branchLike={pullRequest}
              component={component}
              failedConditions={totalFailedConditions}
              isNewCode
              qualityGate={qualityGate}
            />
          </StyledConditionsCard>
        )}
        <StyleMeasuresCard>
          <IssueMeasuresCardInner
            data-testid={`overview__measures-${MetricKey.new_violations}`}
            failed={isIssuesConditionFailed}
            footer={
              issuesCondition &&
              (isIssuesConditionFailed ? (
                <Text colorOverride="echoes-color-text-danger" size="small">
                  {getConditionRequiredLabel(issuesCondition, intl, true)}
                </Text>
              ) : (
                <Text isSubtle size="small">
                  {getConditionRequiredLabel(issuesCondition, intl)}
                </Text>
              ))
            }
            header={intl.formatMessage({ id: 'overview.new_issues' })}
            icon={isIssuesConditionFailed && <TrendUpCircleIcon />}
            metric={MetricKey.new_violations}
            url={issuesUrl}
            value={formatMeasure(issuesCount, MetricType.ShortInteger)}
          />
        </StyleMeasuresCard>
        <StyleMeasuresCard>
          <IssueMeasuresCardInner
            data-testid={`overview__measures-${MetricKey.new_accepted_issues}`}
            disabled={component.needIssueSync}
            footer={
              <Text isSubtle size="small">
                {intl.formatMessage({ id: 'overview.accepted_issues.help' })}
              </Text>
            }
            header={intl.formatMessage({ id: 'overview.accepted_issues' })}
            icon={
              acceptedCount !== undefined && (
                <SnoozeCircleIcon
                  color={
                    acceptedCount === '0' ? 'overviewCardDefaultIcon' : 'overviewCardWarningIcon'
                  }
                />
              )
            }
            metric={MetricKey.new_accepted_issues}
            url={acceptedUrl}
            value={formatMeasure(acceptedCount, MetricType.ShortInteger)}
          />
        </StyleMeasuresCard>
        <StyleMeasuresCard>
          <IssueMeasuresCardInner
            data-testid={`overview__measures-${MetricKey.pull_request_fixed_issues}`}
            disabled={component.needIssueSync}
            footer={
              <Text isSubtle size="small">
                {intl.formatMessage({ id: 'overview.pull_request.fixed_issues.help' })}
              </Text>
            }
            header={
              <>
                {intl.formatMessage({ id: 'overview.pull_request.fixed_issues' })}
                <Tooltip
                  content={
                    <div className="sw-flex sw-flex-col sw-gap-4">
                      <span>
                        {intl.formatMessage({
                          id: 'overview.pull_request.fixed_issues.disclaimer',
                        })}
                      </span>
                      <span>
                        {intl.formatMessage({
                          id: 'overview.pull_request.fixed_issues.disclaimer.2',
                        })}
                      </span>
                    </div>
                  }
                  side={TooltipSide.Top}
                >
                  <span className="sw-typo-default sw-cursor-default">
                    <IconQuestionMark color="echoes-color-icon-subtle" />
                  </span>
                </Tooltip>
              </>
            }
            icon={fixedCount !== undefined && fixedCount !== '0' && <TrendDownCircleIcon />}
            metric={MetricKey.pull_request_fixed_issues}
            url={fixedUrl}
            value={formatMeasure(fixedCount, MetricType.ShortInteger)}
          />
        </StyleMeasuresCard>
        <StyleMeasuresCard>
          <MeasuresCardPercent
            branchLike={pullRequest}
            componentKey={component.key}
            conditionMetric={MetricKey.new_coverage}
            conditions={conditions}
            label="overview.quality_gate.coverage"
            linesMetric={MetricKey.new_lines_to_cover}
            measurementType={MeasurementType.Coverage}
            measures={measures}
            overallConditionMetric={MetricKey.coverage}
            showRequired
            url={getComponentDrilldownUrl({
              componentKey: component.key,
              metric: getMeasurementMetricKey(MeasurementType.Coverage, true),
              branchLike: pullRequest,
              listView: true,
            })}
            useDiffMetric
          />
        </StyleMeasuresCard>
        <StyleMeasuresCardRightBorder>
          <MeasuresCardPercent
            branchLike={pullRequest}
            componentKey={component.key}
            conditionMetric={MetricKey.new_duplicated_lines_density}
            conditions={conditions}
            label="overview.quality_gate.duplications"
            linesMetric={MetricKey.new_lines}
            measurementType={MeasurementType.Duplication}
            measures={measures}
            overallConditionMetric={MetricKey.duplicated_lines_density}
            showRequired
            url={getComponentDrilldownUrl({
              componentKey: component.key,
              metric: getMeasurementMetricKey(MeasurementType.Duplication, true),
              branchLike: pullRequest,
              listView: true,
            })}
            useDiffMetric
          />
        </StyleMeasuresCardRightBorder>
        <div>
          <MeasuresCardNumber
            conditionMetric={MetricKey.new_security_hotspots_reviewed}
            conditions={conditions}
            label={
              newSecurityHotspots === '1'
                ? 'issue.type.SECURITY_HOTSPOT'
                : 'issue.type.SECURITY_HOTSPOT.plural'
            }
            metric={MetricKey.new_security_hotspots}
            showRequired
            url={getComponentSecurityHotspotsUrl(component.key, pullRequest)}
            value={newSecurityHotspots}
          />
        </div>
        <MeasuresCardDependencyRisk
          branchLike={pullRequest}
          component={component}
          conditions={conditions}
          countMetricKey={MetricKey.new_sca_count_any_issue}
          dependencyRiskCount={dependencyRiskCount}
          dependencyRiskRating={dependencyRiskRating}
          ratingMetricKey={MetricKey.new_sca_rating_any_issue}
        />
      </GridContainer>
    </Card>
  );
}
